<template>
  <div id="howtopage" class="howToContainer">
    <div class="header-section">
      <h1 class="mainHeading">Master Nira in Minutes</h1>
      <p class="subheading">Everything you need to know to transform your SVG graphics.</p>
    </div>

    <div class="section-divider">
      <span><i class="fas fa-play-circle"></i> Getting Started</span>
    </div>

    <div class="feature-grid">
      <div class="feature-card">
        <div class="icon-box primary"><i class="fas fa-upload"></i></div>
        <h3>Upload SVG</h3>
        <p>Drop your file or click to upload. Nira supports standard SVG files under 500KB.</p>
      </div>

      <div class="feature-card">
        <div class="icon-box secondary"><i class="fas fa-palette"></i></div>
        <h3>One-Click Palette</h3>
        <p>Apply professionally curated color schemes instantly from the side panel.</p>
      </div>
    </div>

    <div class="section-divider">
      <span><i class="fas fa-magic"></i> Creative Editing</span>
    </div>

    <div class="feature-grid editing-grid">
      <div class="feature-card small">
        <div class="icon-box accent"><i class="fas fa-eye-dropper"></i></div>
        <h3>Individual Elements</h3>
        <p>Click any element to open the color picker and fine-tune its shade or hue.</p>
      </div>

      <div class="feature-card small">
        <div class="icon-box accent"><i class="fas fa-object-group"></i></div>
        <h3>Multi-Selection</h3>
        <p>Hold <kbd>Ctrl</kbd> / <kbd>Cmd</kbd> to select multiple elements and edit them together.</p>
      </div>

      <div class="feature-card small">
        <div class="icon-box accent"><i class="fas fa-arrows-alt"></i></div>
        <h3>Move Elements</h3>
        <p>Position elements anywhere on the canvas using the X and Y translation sliders.</p>
      </div>

      <div class="feature-card small">
        <div class="icon-box accent"><i class="fas fa-expand-arrows-alt"></i></div>
        <h3>Scaling</h3>
        <p>Dynamically resize selected elements while maintaining their vector quality.</p>
      </div>
    </div>

    <div class="section-divider">
      <span><i class="fas fa-check-circle"></i> Export & View</span>
    </div>

    <div class="feature-grid">
      <div class="feature-card">
        <div class="icon-box success"><i class="fas fa-search-plus"></i></div>
        <h3>Dynamic Zoom</h3>
        <p>Use the scroll wheel or zoom buttons to inspect details or view the full composition.</p>
      </div>

      <div class="feature-card">
        <div class="icon-box success"><i class="fas fa-download"></i></div>
        <h3>Quick Export</h3>
        <p>Save your recolored masterpiece as a high-quality SVG with just one click.</p>
      </div>
    </div>

    <div class="footer-actions">
      <el-button class="btn btn-highlight" size="large" @click="goHome()">
        <b> Back to Home</b>
      </el-button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'HowToPage',
  methods: {
    goHome() {
      location.reload();
    }
  }
}
</script>

<style scoped>
.howToContainer {
  max-width: 1000px;
  margin: 0 auto;
  padding: 60px 20px;
  text-align: center;
}

.header-section {
  margin-bottom: 60px;
}

.section-divider {
  display: flex;
  align-items: center;
  margin: 40px 0 30px;
  color: var(--text-dark);
  opacity: 0.6;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  font-size: 0.9rem;
}

.section-divider::before,
.section-divider::after {
  content: "";
  flex: 1;
  height: 1px;
  background: var(--border-color);
}

.section-divider span {
  padding: 0 20px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.feature-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
  margin-bottom: 40px;
}

.editing-grid {
  grid-template-columns: repeat(2, 1fr);
}

.feature-card {
  background: var(--text-light);
  border: 1px solid var(--border-color);
  border-radius: 16px;
  padding: 32px;
  text-align: left;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.feature-card:hover {
  transform: translateY(-5px);
  border-color: var(--primary-color);
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.05), 0 10px 10px -5px rgba(0, 0, 0, 0.02);
}

.icon-box {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  margin-bottom: 8px;
}

.icon-box.primary { background: #e0f2fe; color: #0369a1; }
.icon-box.secondary { background: #f0fdf4; color: #15803d; }
.icon-box.accent { background: #faf5ff; color: #7e22ce; }
.icon-box.success { background: #fefce8; color: #a16207; }

.feature-card h3 {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--text-dark);
}

.feature-card p {
  margin: 0;
  line-height: 1.6;
  color: var(--text-dark);
  opacity: 0.8;
  font-size: 1rem;
}

kbd {
  background-color: #eee;
  border-radius: 3px;
  border: 1px solid #b4b4b4;
  box-shadow: 0 1px 1px rgba(0,0,0,0.2), 0 2px 0 0 rgba(255,255,255,0.7) inset;
  color: #333;
  display: inline-block;
  font-size: 0.85em;
  font-weight: 700;
  line-height: 1;
  padding: 2px 4px;
  white-space: nowrap;
}

.footer-actions {
  margin-top: 60px;
  padding-top: 40px;
  border-top: 1px solid var(--border-color);
}

@media (max-width: 768px) {
  .feature-grid, .editing-grid {
    grid-template-columns: 1fr;
  }
  
  .howToPage {
    padding: 40px 15px;
  }
}

/* Dark Theme support */
html.dark-theme .feature-card {
  background: var(--text-light); /* Actually text-light is dark in dark theme based on App.vue */
}

html.dark-theme .icon-box.primary { background: #0c4a6e; color: #38bdf8; }
html.dark-theme .icon-box.secondary { background: #064e3b; color: #34d399; }
html.dark-theme .icon-box.accent { background: #4c1d95; color: #c084fc; }
html.dark-theme .icon-box.success { background: #422006; color: #fbbf24; }

html.dark-theme kbd {
  background-color: #334155;
  color: #f8fafc;
  border-color: #475569;
}
</style>
